/**
 * The SKOS vocabularies of dim-semantics (vocabularies.ttl, next to the view shapes), indexed by
 * concept scheme and skos:notation, so a view row with dview:scheme can show a value as its concept:
 * the name, the picture (dview:symbol on the concept) and the page to read (foaf:page).
 */

import { Parser } from 'n3';
import type { LangMap } from '@utils/viewShapes.utils';

export const VOCABULARIES_URL = '/semantics-config/vocabularies.ttl';

const SKOS = 'http://www.w3.org/2004/02/skos/core#';
const FOAF_PAGE = 'http://xmlns.com/foaf/0.1/page';
const DVIEW_SYMBOL = 'https://data.banenor.no/shapes/dview#symbol';
const DVIEW_FAMILY = 'https://data.banenor.no/shapes/dview#family';

export interface Concept {
  iri: string;
  label: LangMap;
  notations: string[];
  page: string | null;
  symbol: string | null;
  /** dview:family: the function it shares with other concepts (main-signal for 8.10 to 8.13). */
  family: string | null;
}

/** Scheme IRI → notation → concept. */
export type ConceptIndex = Map<string, Map<string, Concept>>;

export function indexConcepts(turtle: string): ConceptIndex {
  const concepts = new Map<string, Concept & { schemes: string[] }>();
  const of = (iri: string) => {
    if (!concepts.has(iri)) concepts.set(iri, { iri, label: {}, notations: [], page: null, symbol: null, family: null, schemes: [] });
    return concepts.get(iri)!;
  };
  for (const quad of new Parser().parse(turtle)) {
    const subject = quad.subject.value;
    switch (quad.predicate.value) {
      case `${SKOS}prefLabel`:
        of(subject).label[quad.object.termType === 'Literal' ? quad.object.language : ''] = quad.object.value;
        break;
      case `${SKOS}notation`:
        of(subject).notations.push(quad.object.value);
        break;
      case `${SKOS}inScheme`:
        of(subject).schemes.push(quad.object.value);
        break;
      case FOAF_PAGE:
        of(subject).page = quad.object.value;
        break;
      case DVIEW_SYMBOL:
        of(subject).symbol = quad.object.value;
        break;
      case DVIEW_FAMILY:
        of(subject).family = quad.object.value;
        break;
    }
  }
  const index: ConceptIndex = new Map();
  for (const { schemes, ...concept } of concepts.values()) {
    for (const scheme of schemes) {
      const byNotation = index.get(scheme) ?? new Map<string, Concept>();
      index.set(scheme, byNotation);
      for (const notation of concept.notations) byNotation.set(notation, concept);
    }
  }
  return index;
}

let loaded: Promise<ConceptIndex> | null = null;

export function loadConcepts(): Promise<ConceptIndex> {
  loaded ??= fetch(VOCABULARIES_URL)
    .then((response) => (response.ok ? response.text() : ''))
    .then(indexConcepts)
    .catch(() => new Map());
  return loaded;
}

/** A concept's name without the paragraph number it starts with ("§ 8.73 Kilometre signs" → "Kilometre signs"). */
export function shortName(concept: Concept, name: string): string {
  const number = concept.notations.find((notation) => name.startsWith(`§ ${notation} `));
  return number ? name.slice(`§ ${number} `.length) : name;
}

/** The paragraph number of a concept: its notation in the form 8.10. */
export function paragraphOf(concept: Concept): string | null {
  return concept.notations.find((notation) => /^\d+\.\d+$/.test(notation)) ?? null;
}

/** The concept a value stands for in a row's dview:scheme, if any. */
export function conceptOf(index: ConceptIndex | undefined, scheme: string | undefined, value: string | null | undefined): Concept | null {
  if (!index || !scheme || !value) return null;
  return index.get(scheme)?.get(value) ?? null;
}
